// The frame adapter: a React Page or Site (PR 1), and later an App (PR 2), runs on its own named
// isolated origin. The host asks it, over RUN's C14 bridge, what is under a point and where a
// target's boxes are. Everything the frame says is an observation from generated code: it is
// parsed strictly here, bounded, and used only to draw; INS resolves what it means.
import { parseCandidate } from '../../../../src/apps/selection/protocol.mjs';
import type { InspectManifestView } from '../../../../src/apps/selection/manifest-view.mjs';
import { MAX_RECTS, drawable, toSurface, type SurfaceTransform } from '../geometry';
import type { Candidate, GeoIdentity, InspectAdapter, Rect } from '../types';

/**
 * pending-contract (RUN C14 inspect messages; T1-RUN owns transport and validation). The
 * transport binds the exact frame window, origin, document nonce, build and revision; this
 * adapter adds the inspector's own geometry identity on top.
 */
export type FrameBridge = {
  /** What is under `point` (frame CSS px). Resolves with the frame's raw answer, or null. */
  hitTest: (point: { x: number; y: number }, id: GeoIdentity) => Promise<unknown>;
  cancel: (requestId: string) => void;
  /** Ask the frame to keep reporting boxes for these targets (after scroll, resize, re-render). */
  watch: (targetIds: string[]) => void;
  onRects: (listener: (targetId: string, rects: unknown, id: GeoIdentity) => void) => () => void;
  /** A reload, navigation or new build in the frame: the document is a new one. */
  onDocument: (listener: () => void) => () => void;
  scroll: (dx: number, dy: number) => void;
  /** Where the frame's viewport sits in the layer, and its CSS scale (device frames). */
  transform: () => SurfaceTransform;
  /** The layer's visible area, in layer coordinates. */
  viewport: () => Rect;
  /** Ask the frame again for the watched targets' boxes (INS frame bridge). */
  refresh?: () => Promise<void>;
  /**
   * The host moved or scaled the frame (host scroll, device frame, zoom). This is host-local
   * geometry (RUN: not a frame message); INS's bridge may inject it, and otherwise the adapter
   * observes `hostElement` itself. Frame-local boxes are reprojected; in-flight answers stale.
   */
  onTransform?: (listener: () => void) => () => void;
};

/**
 * INS's authorized manifest view (#3287 manifest-view.d.mts) for keyboard pointing, Select
 * parent and the PID <-> canonical target id mapping. Labels and chains come from INS, never
 * from the frame.
 */
export type FrameManifest = Omit<InspectManifestView, 'label'> &
  Partial<Pick<InspectManifestView, 'label'>>;

// The sole strict candidate parser is INS's (#3287 protocol.d.mts): exactly the C16 keys,
// canonical kinds and Id rules, at most eight finite rects, no prototype or extra fields.
export { parseCandidate };

/** Host-local transform observation: the frame element's own size and every scroll above it. */
function observeHost(element: HTMLElement, listener: () => void): () => void {
  const win = element.ownerDocument.defaultView;
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(listener) : null;
  observer?.observe(element);
  // capture: scrolls of any ancestor scroller move the frame in the host
  win?.addEventListener('scroll', listener, { capture: true, passive: true });
  win?.addEventListener('resize', listener);
  return () => {
    observer?.disconnect();
    win?.removeEventListener('scroll', listener, { capture: true });
    win?.removeEventListener('resize', listener);
  };
}

export function createFrameAdapter(
  bridge: FrameBridge,
  manifest?: FrameManifest,
  /** The iframe (or its wrapper) in the host page, for host-local transform observation. */
  hostElement?: HTMLElement,
): InspectAdapter {
  // The latest boxes the frame reported per target, with the identity they were reported for.
  const boxes = new Map<string, { rects: unknown[]; id: GeoIdentity }>();
  const watched = new Set<string>();
  // Frame boxes are in frame coordinates; the host's transform is applied when drawing, so a
  // host scroll or resize (a new epoch) doesn't stale them. A new session, revision or
  // document does.
  const fresh = (id: GeoIdentity, current: GeoIdentity) =>
    id.sessionId === current.sessionId &&
    id.revision === current.revision &&
    id.docGeneration === current.docGeneration;

  // Watches go out as canonical target ids; reports come back keyed by them and are filed
  // under the host's node id. Without a trusted mapping no box is drawn rather than guessed.
  const toTarget = (nodeId: string) => manifest?.targetIdFor(nodeId) ?? null;
  const sendWatch = () =>
    bridge.watch([...watched].map(toTarget).filter((t): t is string => t !== null));
  const offReports = bridge.onRects((reportedTargetId, rects, id) => {
    const targetId = manifest?.nodeIdFor(reportedTargetId) ?? null;
    if (!targetId || !watched.has(targetId) || !Array.isArray(rects) || rects.length > MAX_RECTS)
      return;
    // A late report never overwrites a newer observation for the same target.
    const known = boxes.get(targetId);
    if (
      known &&
      (id.docGeneration < known.id.docGeneration ||
        (id.docGeneration === known.id.docGeneration && id.epoch < known.id.epoch))
    )
      return;
    boxes.set(targetId, { rects, id });
  });

  return {
    surface: 'frame',
    async hitTest(point, id) {
      const raw = await bridge.hitTest(toSurface(point, bridge.transform()), id);
      const parsed = parseCandidate(raw);
      if (!parsed) return null;
      const rects = drawable(parsed.rects, bridge.transform(), bridge.viewport());
      return { ...parsed, rects };
    },
    cancel: (requestId) => bridge.cancel(requestId),
    rects(targetId, id) {
      if (!watched.has(targetId)) {
        watched.add(targetId);
        sendWatch();
      }
      const known = boxes.get(targetId);
      // Boxes reported for an older document, epoch or revision are not drawn.
      if (!known || !fresh(known.id, id)) return [];
      return drawable(known.rects, bridge.transform(), bridge.viewport());
    },
    chain: (nodeId) => manifest?.chain(nodeId) ?? [],
    refresh: () => void bridge.refresh?.().catch(() => {}),
    list: manifest ? () => manifest.list() : undefined,
    retain(nodeIds) {
      // The watch set is the current selection, never a growing history.
      const keep = new Set(nodeIds.slice(0, 16));
      let changed = keep.size !== watched.size;
      for (const id of [...watched])
        if (!keep.has(id)) {
          watched.delete(id);
          boxes.delete(id);
          changed = true;
        }
      for (const id of keep) if (!watched.has(id)) watched.add(id);
      if (changed) sendWatch();
    },
    dispose() {
      offReports();
      watched.clear();
      boxes.clear();
    },
    scrollIntent: (dx, dy) => bridge.scroll(dx, dy),
    subscribe(onChange) {
      const offDocument = bridge.onDocument(() => {
        // A replaced document: every box and every watch belongs to the old one.
        boxes.clear();
        watched.clear();
        onChange('document');
      });
      // New boxes only redraw: a frame (animating or hostile) can't stale a click in flight.
      const offRects = bridge.onRects(() => onChange('rects'));
      const onTransform = () => onChange('geometry');
      const offTransform =
        bridge.onTransform?.(onTransform) ??
        (hostElement ? observeHost(hostElement, onTransform) : undefined);
      return () => {
        offDocument();
        offRects();
        offTransform?.();
      };
    },
  };
}
